import type { ContextProviderComponent } from "solid-js";
import { createContext } from "solid-js";
import type { AxiosError, AxiosInstance } from "axios";
import axios from "axios";
import { createAuthRefresh } from "axios-auth-refresh";
import { useAuthDispatch, useAuthState } from "./auth";
import { API_BASE_URL } from "../constants";
import { initializedContext } from "../utils/initializedUtil";
import type { MoreAppError } from "../MoreAppError";
import { handleApiError } from "../MoreAppError";
import { isNil } from "lodash-es";

const Context = createContext<AxiosInstance>();

export const ClientProvider: ContextProviderComponent<undefined> = (props) => {
  const { refreshAccessToken, getCurrentUser } = useAuthDispatch();
  const { impersonation } = useAuthState();
  const client = axios.create({
    baseURL: API_BASE_URL,
    timeout: 50_000, // ms
  });

  createAuthRefresh(
    client,
    async () => {
      try {
        await refreshAccessToken();
      } catch (e) {
        throw Error("Unable to refresh access token on failing request", { cause: e });
      }
    },
    // With 'true' only the first of concurrent 401s is retried, the others are rejected. With 'false' all of them are
    // retried and still share a single refresh. https://github.com/Flyrell/axios-auth-refresh/issues/307
    { deduplicateRefresh: false },
  );

  client.interceptors.request.use(async (request) => {
    const user = await getCurrentUser();
    if (!isNil(user?.access_token)) {
      request.headers.Authorization = `Bearer ${user.access_token}`;
    }
    return request;
  });

  client.interceptors.request.use(async (request) => {
    request.headers["X-More-Consumer-Override"] = impersonation()?.value;
    return request;
  });

  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError<MoreAppError>) => Promise.reject(handleApiError(error)),
  );

  return <Context.Provider value={client}>{props.children}</Context.Provider>;
};

export const useClient = () => initializedContext(Context, "useClient", "ClientProvider");
